import { FC, useCallback, useEffect } from 'react';
import styled from 'styled-components/macro';
import { FontFamily, useZakeke } from 'zakeke-configurator-react';
import { Button, Columns, Icon, TextArea } from './Atomic';
import Select, { components, GroupBase, OptionProps, SingleValueProps } from 'react-select';
import { useState } from 'react';
import { debounce } from 'lodash';

import { TimesSolid } from './../../icons';
import { BoldSolid } from './../../icons';
import { ItalicSolid } from './../../icons/';
import { BezierCurveSolid } from './../../icons';
import { FormControl } from './FormControl';
import ColorPicker from './colorpicker';
import { useTranslation } from 'react-i18next';

export interface EditTextItem {
  guid: string;
  name: string;
  text: string;
  fillColor: string;
  fontFamily: string;
  fontWeight: string;
  fontSize: number;
  isTextOnPath: boolean;
  constraints: { [key: string]: any } | null;
}

const defaultColorsPalette = ['#000000', '#FFFFFF'];

enum ItemType {
  Text = 0,
  Image = 1,
}

export interface TextItem {
  type: ItemType;
  areaId: number;
  guid: string;
  name: string;
  text: string;
  strokeColor: string;
  strokeWidth: number;
  fillColor: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: string;
  fontStyle: string | undefined;
  fontStretch: string;
  justification: string;
  isTextOnPath: boolean;
  constraints: {
    [key: string]: any;
  } | null;
}

const ItemTextContainer = styled.div`
  border-radius: 5px;
  margin: 20px 0 10px;
  padding: 5px 10px 20px;
  background-color: #eeeeee;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -15px;
    border-bottom: 1px dotted #8c8a8a;
  }
`;

const TextToolsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  grid-gap: 5px;
  padding-bottom: 0px;
`;

const TextButtonsContainer = styled.div`
  width: 50%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 5px;
`;

const ColorPickerContainer = styled.div`
  margin-right: 5px;
  width: calc(50% - 30px);
`;

const ColorsContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const SinglePaletteItem = styled.div<{ color: string; selected: boolean }>`
  width: 19px;
  height: 19px;
  background-color: ${(props) => props.color};
  border: 1px lightgray solid;
  border-radius: 4px;
  cursor: pointer;

  ${(props) => props.selected && `border: 1px black solid;`}

  &:hover {
    opacity: 0.6;
  }
`;

const TextColorsContainer = styled.div<{ isDefaultPalette?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;
  ${(props) =>
    !props.isDefaultPalette &&
    `
    grid-template-columns: repeat(auto-fill,minmax(20px,1fr));
    grid-gap: 5px;`};
  /* grid-template-columns: repeat(auto-fill,minmax(20px,1fr)); */
  width: 100%;
`;

const OptionContainer = styled(components.Option)`
  background-color: white !important;

  &:hover {
    background-color: #ddd !important;
  }

  img {
    max-width: 100%;
    height: 24px;
    object-fit: contain;
  }
`;

const SingleValueContainer = styled(components.SingleValue)`
  img {
    max-width: 100%;
    height: 24px;
    object-fit: contain;
  }
`;

const FontOption = (props: JSX.IntrinsicAttributes & OptionProps<any, boolean, GroupBase<any>>) => {
  return <OptionContainer {...props}>{<img src={props.data.imageUrl} alt={props.data.name} />}</OptionContainer>;
};

const FontSingleValue = (props: JSX.IntrinsicAttributes & SingleValueProps<any, boolean, GroupBase<any>>) => {
  return <SingleValueContainer {...props}>{<img src={props.data.imageUrl} alt={props.data.name} />}</SingleValueContainer>;
};

const ItemText: FC<{ item: EditTextItem; handleItemPropChange: any; fonts?: FontFamily[]; hideRemoveButton?: boolean }> = ({
  item,
  handleItemPropChange,
  hideRemoveButton,
}) => {
  const { fonts, getSanitationText, getPrintingMethodsRestrictions } = useZakeke();
  const [t] = useTranslation();
  const [dirtyCharInserted, setDirtyCharInserted] = useState([] as string[]);
  let currentFont = fonts?.find((x) => x.name === item.fontFamily);
  const textRestrictions = getPrintingMethodsRestrictions();

  const constraints = item.constraints;
  const canEdit = constraints?.canEdit ?? true;
  const hasCurvedText = item.isTextOnPath;
  const isUpperCase = constraints?.toUppercase ?? false;

  // Used for performance cache
  const [fillColor, setFillColor] = useState(item.fillColor);

  const weightData = typeof item.fontWeight === 'number' ? ['normal', 'normal'] : item.fontWeight.split(' ');
  const isBold = weightData.length > 1 ? weightData[1] === 'bold' : weightData[0] === 'bold';
  const isItalic = weightData.length > 1 ? weightData[0] === 'italic' : false;

  const setItemTextDebounced = (value: string) => {
    handleItemPropChange?.(item, 'text', isUpperCase ? value.toUpperCase() : value);
    debounce(() => {
      const initialText = value;
      const sanitizationInfo = currentFont
        ? getSanitationText(currentFont, value)
        : {
            sanitizedText: value,
            dirtyChars: [],
          };
      setDirtyCharInserted(sanitizationInfo.dirtyChars);
      const text = sanitizationInfo.sanitizedText;

      if (text !== initialText) {
        handleItemPropChange?.(item, 'text', isUpperCase ? text.toUpperCase() : text);
      }
    }, 500)();
  };

  function wrapperJoin(array: any[], separator: string, prefix: string, suffix: string) {
    let result = '';
    for (let i = 0; i < array.length; i++) {
      result += prefix + array[i] + suffix;
      if (i < array.length - 1) result += separator;
    }
    return result;
  }

  const handleFontChange = (font: string) => {
    handleItemPropChange(item, 'font-family', font);
    currentFont = fonts?.find((x) => x.name === font);
    setItemTextDebounced(item.text);
  };

  useEffect(() => {
    handleFontChange(item.fontFamily);
    //eslint-disable-next-line
  }, []);

  if (item)
    return (
      <ItemTextContainer>
        <FormControl
          label={t(item.name) || 'Text'}
          rightComponent={
            !hideRemoveButton &&
            item.constraints!.canDelete && (
              <Icon onClick={() => handleItemPropChange(item, 'remove')}>
                <TimesSolid />
              </Icon>
            )
          }
        >
          <TextArea
            value={isUpperCase ? item.text.toUpperCase() : item.text}
            onChange={(e) => {
              e.currentTarget.value = e.currentTarget.value.replace('⠀', '');
              setItemTextDebounced(e.currentTarget.value);
            }}
            maxLength={!item.constraints ? null : item.constraints.maxNrChars || null}
            disabled={!canEdit}
          />
          {dirtyCharInserted.length > 0 && currentFont && (
            <div style={{ color: 'red' }}>
              {`The following characters have been removed as they are not supported by the font ${currentFont.name}: ${wrapperJoin(
                dirtyCharInserted,
                ',',
                '"',
                '"'
              )}`}
            </div>
          )}
        </FormControl>
        <TextToolsContainer>
          {(!constraints || constraints.canChangeFontFamily) && (
            <FormControl label={'Font'}>
              <Select
                styles={{
                  container: (base) => ({
                    ...base,
                    minWidth: 180,
                    zIndex: 11,
                  }),
                }}
                isSearchable={false}
                options={fonts}
                menuPosition='absolute'
                components={{
                  Option: FontOption,
                  SingleValue: FontSingleValue,
                }}
                value={[fonts!.find((x) => x.name === item.fontFamily)]}
                onChange={(font: any) => handleItemPropChange(item, 'font-family', font.name)}
              />
            </FormControl>
          )}
          {/* <TextButtonsContainer>
            {(!constraints || constraints.canChangeFontWeight) && (
              <FormControl label={'Style'}>
                <Columns columns={2}>
                  <Button outline selected={isBold} onClick={() => handleItemPropChange(item, 'font-bold', !isBold)}>
                    <Icon>
                      <BoldSolid />
                    </Icon>
                  </Button>
                  <Button outline selected={isItalic} onClick={() => handleItemPropChange(item, 'font-italic', !isItalic)}>
                    <Icon>
                      <ItalicSolid />
                    </Icon>
                  </Button>
                </Columns>
              </FormControl>
            )}
            {(!constraints || constraints.canChangeTextPathMode) && (
              <FormControl label={'Curved'}>
                <Button outline selected={hasCurvedText} onClick={() => handleItemPropChange(item, 'text-path', !hasCurvedText)}>
                  <Icon>
                    <BezierCurveSolid />
                  </Icon>
                </Button>
              </FormControl>
            )}
          </TextButtonsContainer> */}

          {(!textRestrictions.disableTextColors || !(textRestrictions.disableTextColors && textRestrictions.textColors.length === 1)) &&
            !!item.constraints?.canChangeFontColor && (
              <FormControl label={'Color'}>
                <ColorsContainer>
                  {!textRestrictions.disableTextColors && (
                    <ColorPickerContainer>
                      <ColorPicker
                        color={fillColor}
                        onChange={(color: any) => {
                          // handleFillColorChange(color);
                          handleItemPropChange(item, 'font-color', color);
                          setFillColor(color);
                        }}
                      />
                    </ColorPickerContainer>
                  )}
                  {!textRestrictions.disableTextColors && (
                    <TextColorsContainer isDefaultPalette>
                      {defaultColorsPalette.map((hex) => (
                        <SinglePaletteItem
                          key={hex}
                          onClick={() => {
                            handleItemPropChange(item, 'font-color', hex);
                            setFillColor(hex);
                          }}
                          selected={hex === fillColor}
                          color={hex}
                        />
                      ))}
                    </TextColorsContainer>
                  )}

                  {textRestrictions.disableTextColors && (
                    <TextColorsContainer>
                      {textRestrictions.textColors.map((textColor) => (
                        <SinglePaletteItem
                          key={textColor.colorCode}
                          onClick={() => {
                            handleItemPropChange(item, 'font-color', textColor.colorCode);
                            setFillColor(textColor.colorCode);
                          }}
                          selected={textColor.colorCode === fillColor}
                          color={textColor.colorCode}
                        />
                      ))}
                    </TextColorsContainer>
                  )}
                </ColorsContainer>
              </FormControl>
            )}
        </TextToolsContainer>{' '}
        {/* if enabling curved text tools move this closing tag above the color container */}
      </ItemTextContainer>
    );
  else return null;
};

export default ItemText;
